import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Badge,
  Box,
  Card,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { LuArrowRight, LuCalendar, LuClock, LuMapPin } from "react-icons/lu";

import { Button } from "components/ui/Button";
import { useMainActividadesPublicas } from "features/main/actividades/hooks";
import { formatFechaYYYYMMDD, formatHoraHHMM } from "utils/formatters";
import { SectionHeading, SectionShell } from "../Inicio/components";

const EMPTY_MSG = "Aun no se ha planeado una actividad para este fin de semana.";
const DEFAULT_COVER = "https://images.unsplash.com/photo-1521412644187-c49fa049e84d?auto=format&fit=crop&w=1200&q=60";

const ActivityHero = () => (
  <Box as="section" position="relative" overflow="hidden" bg="public.bg.canvas" color="public.fg.default" px={{ base: 5, md: 10 }} pt={{ base: "82px", md: "116px" }} pb={{ base: 7, md: 11 }} borderBottomWidth="1px" borderColor="public.border.subtle">
    <Box position="absolute" w={{ base: "160px", md: "330px" }} h={{ base: "62px", md: "105px" }} rounded="full" bg="#eadde8" _dark={{ bg: "#3d3040" }} right={{ base: "-82px", md: "7%" }} bottom={{ base: "-30px", md: "-58px" }} transform="rotate(10deg)" opacity=".72" />
    <Stack position="relative" zIndex="1" maxW="7xl" mx="auto">
      <Heading as="h1" fontFamily="display" fontSize={{ base: "38px", md: "56px" }} lineHeight="1" letterSpacing="-.03em">Actividades</Heading>
    </Stack>
  </Box>
);

const ActivityMeta = ({ icon, children }) => (
  <HStack align="start" gap="2.5" color="whiteAlpha.900">
    <Icon as={icon} boxSize="4" mt="0.5" color="brand.redFg" />
    <Text fontSize="sm" lineHeight="1.5">
      {children}
    </Text>
  </HStack>
);

function GradoCard({ nivel }) {
  const actividad = nivel?.actividadPrincipal ?? null;
  const hasActividad = !!actividad;
  const bgSrc = actividad?.coverUrl || DEFAULT_COVER;

  return (
    <Card.Root
      as={RouterLink}
      to={`/actividades/${nivel.nivelId}`}
      aria-label={`Ver actividades de ${nivel.nivelNombre}`}
      role="group"
      overflow="hidden"
      rounded="32px 14px 32px 14px"
      borderWidth="1px"
      borderColor="public.border.default"
      bg="gray.950"
      color="white"
      minH={{ base: "360px", md: "430px" }}
      textDecoration="none"
      transition="transform 220ms ease, box-shadow 220ms ease"
      _hover={{ textDecoration: "none", transform: "translateY(-6px)", shadow: "cardLift" }}
      _focusVisible={{ outline: "2px solid", outlineColor: "brand.red", outlineOffset: "3px" }}
    >
      <Box position="relative" minH="inherit" overflow="hidden">
        <Image
          src={bgSrc}
          alt={hasActividad ? actividad.titulo : nivel.nivelNombre}
          position="absolute"
          inset="0"
          w="full"
          h="full"
          objectFit="cover"
          transition="transform 520ms ease"
          _groupHover={{ transform: "scale(1.07)" }}
        />
        <Box position="absolute" inset="0" bg="linear-gradient(to top, rgba(10,0,0,.80), rgba(0,0,0,0.12) 58%)" />
        <Stack position="absolute" inset="0" p="5" justify="space-between">
          <Flex justify="space-between" align="start" gap="3">
            <Badge bg="brand.red" color="white" rounded="full" px="3" py="1">
              {nivel.nivelNombre} grado
            </Badge>
            {hasActividad ? (
              <Badge bg="whiteAlpha.900" color="gray.900" rounded="full" px="3" py="1">
                Esta semana
              </Badge>
            ) : null}
          </Flex>

          <Stack gap="4">
            <Stack gap="2">
              <Heading as="h3" fontFamily="display" fontSize={{ base: "3xl", md: "4xl" }} lineHeight="1">
                {nivel.nivelNombre}
              </Heading>
              <Text color="whiteAlpha.900" fontSize="sm" fontWeight="800" textTransform="uppercase">
                {hasActividad ? actividad.titulo : EMPTY_MSG}
              </Text>
            </Stack>

            {hasActividad ? (
              <Stack gap="2">
                <ActivityMeta icon={LuCalendar}>{formatFechaYYYYMMDD(actividad.fecha)}</ActivityMeta>
                <ActivityMeta icon={LuClock}>
                  {formatHoraHHMM(actividad.horaInicio)} - {formatHoraHHMM(actividad.horaFin)}
                </ActivityMeta>
                {actividad.lugar ? <ActivityMeta icon={LuMapPin}>{actividad.lugar}</ActivityMeta> : null}
              </Stack>
            ) : null}

            <HStack color="white" fontWeight="800" gap="2">
              <Text>Ver grado</Text>
              <Icon as={LuArrowRight} boxSize="4" transition="transform 180ms ease" _groupHover={{ transform: "translateX(4px)" }} />
            </HStack>
          </Stack>
        </Stack>
      </Box>
    </Card.Root>
  );
}

const CampusSection = ({ campus, index }) => {
  const niveles = Array.isArray(campus.niveles) ? campus.niveles : [];
  const bg = index % 2 === 0 ? "public.bg.canvas" : "public.bg.panel";

  return (
    <SectionShell bg={bg}><Stack gap={{ base: 8, md: 10 }}>
      <SectionHeading
        eyebrow="Campus"
        title={campus.campusNombre}
        description="Selecciona el grado para ver la actividad de la semana, instructores, galeria y actividades anteriores."
      />
      {niveles.length ? (
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap="5">
          {niveles.map((nivel) => (
            <GradoCard key={nivel.nivelId} nivel={nivel} />
          ))}
        </SimpleGrid>
      ) : (
        <Card.Root bg="public.bg.elevated" borderWidth="1px" borderColor="public.border.default" rounded="24px">
          <Card.Body p="8">
            <Text color="public.fg.muted">No hay niveles disponibles para este campus.</Text>
          </Card.Body>
        </Card.Root>
      )}
    </Stack></SectionShell>
  );
};

const RegisterCta = () => (
  <Box bg="public.bg.canvas" px={{ base: 5, md: 10 }} py={{ base: 16, md: 24 }}><Box bg="gray.950" color="white" position="relative" overflow="hidden" maxW="6xl" mx="auto" rounded="48px 18px 48px 18px">
    <Box position="absolute" inset="0" bgImage="radial-gradient(circle, rgba(255,255,255,0.16) 1px, transparent 1px)" bgSize="28px 28px" />
    <Stack position="relative" zIndex="1" maxW="4xl" mx="auto" px={{ base: 5, md: 10 }} py={{ base: 16, md: 20 }} align="center" textAlign="center" gap="5">
      <Heading as="h2" fontFamily="display" fontSize={{ base: "3xl", md: "5xl" }} lineHeight="1.02">
        Listo para vivir la aventura?
      </Heading>
      <Text color="whiteAlpha.900" fontSize={{ base: "md", md: "lg" }} lineHeight="1.7" maxW="2xl">
        Registra a tu hijo para recibir informacion de actividades, campamentos y eventos del club.
      </Text>
      <Button as={RouterLink} to="/registro" variant="ghost" color="transparent" size="lg">
        Registrarme
        <Icon as={LuArrowRight} boxSize="4" />
      </Button>
    </Stack></Box>
  </Box>
);

const Actividades = () => {
  const q = useMainActividadesPublicas();
  const campuses = React.useMemo(() => {
    const rows = Array.isArray(q.data?.campuses) ? q.data.campuses : [];
    return [...rows].sort((a, b) => {
      const aCount = Array.isArray(a?.niveles) ? a.niveles.length : 0;
      const bCount = Array.isArray(b?.niveles) ? b.niveles.length : 0;
      if (aCount !== bCount) return bCount - aCount;
      return String(a?.campusNombre || "").localeCompare(String(b?.campusNombre || ""));
    });
  }, [q.data?.campuses]);

  return (
    <Box bg="public.bg.canvas" color="public.fg.default">
      <ActivityHero />

      {q.isLoading ? (
        <Flex justify="center" py="16">
          <Spinner size="xl" color="brand.red" />
        </Flex>
      ) : null}

      {q.isError ? (
        <Text textAlign="center" color="red.500" py="10">
          No fue posible cargar las actividades.
        </Text>
      ) : null}

      {!q.isLoading && !q.isError && campuses.length === 0 ? (
        <SectionShell>
          <Card.Root bg="public.bg.elevated" borderWidth="1px" borderColor="public.border.default" rounded="24px">
            <Card.Body p="8">
              <Text textAlign="center" color="public.fg.muted">
                No hay niveles disponibles para el periodo actual.
              </Text>
            </Card.Body>
          </Card.Root>
        </SectionShell>
      ) : null}

      {!q.isLoading && !q.isError
        ? campuses.map((campus, index) => <CampusSection key={campus.campusId} campus={campus} index={index} />)
        : null}

      {!q.isLoading && !q.isError ? <RegisterCta /> : null}
    </Box>
  );
};

export default Actividades;
